/**
 * Cross-module X XWP-54 (CP §15.14, XO-09) — « Jours restants » of a dispensed treatment.
 *
 *   jours restants = (Date délivrance + Durée) − aujourd'hui
 *
 * counted in CALENDAR DAYS OF THE OFFICINE ZONE (Africa/Algiers by default): a delivery at 23:30 in
 * Algiers is that Algiers day, whatever the server's or the browser's own zone. The end date is the
 * delivery day + Durée days (a 30-day treatment delivered on the 1st ends on the 31st: 30 days left on
 * the 1st, 0 on the 31st — « Fin aujourd'hui », −2 two days later — « Dépassé »).
 *
 * A missing or unusable value is never guessed (CP §15.14): no delivery date, or a Durée that is not a
 * whole number of days > 0, gives `null` and the état « À compléter » — the screen asks for it.
 *
 * One implementation for the register (server, GET /ordonnancier), the treatment read model
 * (XWP-56) and the screens (XWP-55, XWP-59). The server imports its byte-identical copy
 * (apps/server/src/utils/traitement, `npm run sync:traitement`).
 */

/** The officine's zone when the caller passes none (the server passes its CONFIG FUSEAU_OFFICINE). */
export const FUSEAU_OFFICINE_DEFAUT = "Africa/Algiers";

/** États of a treatment's days remaining — codes only; the French labels live in each app's copy. */
export const ETAT_JOURS_RESTANTS = Object.freeze({
  A_COMPLETER: "A_COMPLETER", // no delivery date or no usable Durée — ask for it
  EN_COURS: "EN_COURS", // ends after today
  FIN_AUJOURDHUI: "FIN_AUJOURDHUI", // ends today (0 day left)
  DEPASSE: "DEPASSE", // ended before today (negative)
});

/**
 * The display highlight of a treatment about to end (XD-219: « the 3-day highlight is a separate display
 * constant », distinct from the Ailaji refill trigger `AALAJI_REFILL.THRESHOLD_JOURS`): a treatment with
 * 1…3 days left is shown « bientôt terminé » on the register (XWP-55) and the follow-up view (XWP-59).
 * Platform-governed display constant — never a business rule.
 */
export const SEUIL_FIN_PROCHE_JOURS = 3;

const JOUR_MS = 86_400_000;
const FORMATS = new Map();

function formatJour(fuseau) {
  let f = FORMATS.get(fuseau);
  if (!f) {
    // en-CA formats a date as YYYY-MM-DD
    f = new Intl.DateTimeFormat("en-CA", { timeZone: fuseau, year: "numeric", month: "2-digit", day: "2-digit" });
    FORMATS.set(fuseau, f);
  }
  return f;
}

function enDate(valeur) {
  if (valeur == null || valeur === "") return null;
  const d = valeur instanceof Date ? valeur : new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The calendar day (YYYY-MM-DD) of an instant in a zone, or null for a missing / invalid instant.
 * @param {Date|string|number|null|undefined} instant
 * @param {string} [fuseau]
 * @returns {string|null}
 */
export function jourCivil(instant, fuseau = FUSEAU_OFFICINE_DEFAUT) {
  const d = enDate(instant);
  if (!d) return null;
  const parts = formatJour(fuseau).formatToParts(d);
  const v = (type) => parts.find((p) => p.type === type)?.value;
  return `${v("year")}-${v("month")}-${v("day")}`;
}

function jourVersUtc(jour) {
  const [a, m, j] = jour.split("-").map(Number);
  return Date.UTC(a, m - 1, j);
}

function utcVersJour(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * A usable Durée: a whole number of days > 0 (a number or its decimal string), else null.
 * @param {unknown} duree
 * @returns {number|null}
 */
export function dureeJoursValide(duree) {
  if (duree == null || duree === "" || typeof duree === "boolean") return null;
  const n = typeof duree === "number" ? duree : Number(String(duree).trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * The day the treatment ends: the delivery day (officine zone) + Durée days, or null.
 * @param {{ date_delivrance: Date|string|number|null|undefined, duree_jours: unknown, fuseau?: string }} p
 * @returns {string|null}
 */
export function dateFinTraitement({ date_delivrance, duree_jours, fuseau = FUSEAU_OFFICINE_DEFAUT }) {
  const debut = jourCivil(date_delivrance, fuseau);
  const duree = dureeJoursValide(duree_jours);
  if (!debut || duree == null) return null;
  return utcVersJour(jourVersUtc(debut) + duree * JOUR_MS);
}

/**
 * (Date délivrance + Durée) − today, in officine calendar days; null when a value is missing.
 * @param {{ date_delivrance: Date|string|number|null|undefined, duree_jours: unknown, maintenant?: Date|string|number, fuseau?: string }} p
 * @returns {number|null}
 */
export function joursRestants({ date_delivrance, duree_jours, maintenant = new Date(), fuseau = FUSEAU_OFFICINE_DEFAUT }) {
  const fin = dateFinTraitement({ date_delivrance, duree_jours, fuseau });
  const aujourdhui = jourCivil(maintenant, fuseau);
  if (!fin || !aujourdhui) return null;
  return Math.round((jourVersUtc(fin) - jourVersUtc(aujourdhui)) / JOUR_MS);
}

/**
 * The état of a days-remaining value (null → À compléter).
 * @param {number|null|undefined} jours
 * @returns {string}
 */
export function etatJoursRestants(jours) {
  if (jours == null || !Number.isFinite(jours)) return ETAT_JOURS_RESTANTS.A_COMPLETER;
  if (jours > 0) return ETAT_JOURS_RESTANTS.EN_COURS;
  if (jours === 0) return ETAT_JOURS_RESTANTS.FIN_AUJOURDHUI;
  return ETAT_JOURS_RESTANTS.DEPASSE;
}

/**
 * The register / read-model block of one dispensed line: end day, days remaining and état.
 * @param {{ date_delivrance: Date|string|number|null|undefined, duree_jours: unknown, maintenant?: Date|string|number, fuseau?: string }} p
 * @returns {{ date_fin: string|null, jours_restants: number|null, etat: string }}
 */
export function suiviTraitement({ date_delivrance, duree_jours, maintenant = new Date(), fuseau = FUSEAU_OFFICINE_DEFAUT }) {
  const date_fin = dateFinTraitement({ date_delivrance, duree_jours, fuseau });
  const jours_restants = joursRestants({ date_delivrance, duree_jours, maintenant, fuseau });
  return { date_fin, jours_restants, etat: etatJoursRestants(jours_restants) };
}

/**
 * A running treatment that ends within `SEUIL_FIN_PROCHE_JOURS` days (1…3 left) — the display highlight.
 * @param {number|null|undefined} jours
 * @returns {boolean}
 */
export function finProche(jours, seuil = SEUIL_FIN_PROCHE_JOURS) {
  return Number.isFinite(jours) && jours > 0 && jours <= seuil;
}
